// 🔍 Subscription Diagnostics Utility
// Helper to diagnose subscription creation and display issues

import { doc, getDoc, collection, getDocs } from 'firebase/firestore';

/**
 * Check subscription status for a company
 * @param {Object} db - Firestore database instance
 * @param {string} companyId - Company ID to check
 * @returns {Promise<Object>} - Diagnostic results
 */
export const diagnoseSubscription = async (db, companyId) => {
  console.log('🔍 Starting subscription diagnostics for company:', companyId);
  
  const results = {
    companyId,
    timestamp: new Date().toISOString(),
    checks: {}
  };

  try {
    // 1. Check if company document exists
    console.log('📋 Checking company document...');
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    results.checks.companyExists = companySnap.exists();
    if (companySnap.exists()) {
      const companyData = companySnap.data();
      results.checks.companyPlan = companyData.plan || 'NOT SET';
      results.checks.companyCreatedAt = companyData.createdAt?.toDate?.() || companyData.createdAt;
      console.log('✅ Company document exists');
      console.log('   Plan:', results.checks.companyPlan);
      console.log('   Created:', results.checks.companyCreatedAt);
    } else {
      console.log('❌ Company document does not exist');
    }

    // 2. Check if subscription_details subcollection exists
    console.log('📋 Checking subscription_details subcollection...');
    const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    results.checks.subscriptionExists = subscriptionSnap.exists();
    if (subscriptionSnap.exists()) {
      const subscriptionData = subscriptionSnap.data();
      results.checks.subscriptionData = {
        planId: subscriptionData.basePlan?.planId || subscriptionData.plan,
        planName: subscriptionData.basePlan?.planName,
        isTrial: subscriptionData.basePlan?.isTrial || subscriptionData.isTrialActive,
        trialEndDate: subscriptionData.basePlan?.trialEndDate || subscriptionData.trialEndDate,
        createdAt: subscriptionData.createdAt?.toDate?.() || subscriptionData.createdAt,
        status: subscriptionData.status
      };
      console.log('✅ Subscription document exists');
      console.log('   Plan ID:', results.checks.subscriptionData.planId);
      console.log('   Plan Name:', results.checks.subscriptionData.planName);
      console.log('   Is Trial:', results.checks.subscriptionData.isTrial);
      console.log('   Created:', results.checks.subscriptionData.createdAt);
    } else {
      console.log('❌ Subscription document does not exist');
    }

    // 3. Check timing difference
    if (results.checks.companyExists && results.checks.subscriptionExists) {
      const companyTime = new Date(results.checks.companyCreatedAt).getTime();
      const subscriptionTime = new Date(results.checks.subscriptionData.createdAt).getTime();
      const timeDiffSeconds = Math.abs(subscriptionTime - companyTime) / 1000;
      
      results.checks.timingDifference = {
        seconds: timeDiffSeconds,
        note: timeDiffSeconds > 5 ? 'SIGNIFICANT_DELAY' : 'NORMAL'
      };
      console.log('⏱️  Timing difference:', timeDiffSeconds, 'seconds');
    }

    // 4. Check if subscription_details collection has any documents
    console.log('📋 Listing all documents in subscription_details...');
    try {
      const subscriptionDetailsRef = collection(db, 'companies', companyId, 'subscription_details');
      const subscriptionDetailsSnap = await getDocs(subscriptionDetailsRef);
      results.checks.subscriptionDetailsDocCount = subscriptionDetailsSnap.size;
      results.checks.subscriptionDetailsDocIds = subscriptionDetailsSnap.docs.map(d => d.id);
      console.log('📊 Found', subscriptionDetailsSnap.size, 'documents in subscription_details');
      console.log('   Document IDs:', results.checks.subscriptionDetailsDocIds);
    } catch (error) {
      results.checks.subscriptionDetailsError = error.message;
      console.log('❌ Error listing subscription_details:', error.message);
    }

    // Summary
    console.log('\n📊 DIAGNOSTIC SUMMARY:');
    console.log('='.repeat(50));
    if (results.checks.companyExists && results.checks.subscriptionExists) {
      console.log('✅ Both company and subscription documents exist');
      console.log('✅ Everything looks good!');
    } else if (results.checks.companyExists && !results.checks.subscriptionExists) {
      console.log('⚠️  Company exists but subscription document is MISSING');
      console.log('   This is the issue! The subscription was not created properly.');
    } else if (!results.checks.companyExists) {
      console.log('❌ Company document does not exist');
      console.log('   Invalid company ID or permission issue');
    }
    console.log('='.repeat(50));

    return {
      success: true,
      results
    };

  } catch (error) {
    console.error('❌ Error during diagnostics:', error);
    return {
      success: false,
      error: error.message,
      results
    };
  }
};

/**
 * Quick subscription check - returns true if subscription exists, false otherwise
 */
export const hasSubscription = async (db, companyId) => {
  try {
    const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
    const subscriptionSnap = await getDoc(subscriptionRef);
    return subscriptionSnap.exists();
  } catch (error) {
    console.error('Error checking subscription:', error);
    return false;
  }
};

/**
 * Manual subscription fix - creates subscription if missing
 */
export const fixMissingSubscription = async (db, companyId, userId) => {
  console.log('🔧 Attempting to fix missing subscription for company:', companyId);
  
  try {
    const { createTrialSubscription } = await import('./trialSubscriptionUtils');
    const result = await createTrialSubscription(db, companyId, userId);
    
    if (result.success) {
      console.log('✅ Subscription fixed successfully!');
      return { success: true, message: 'Subscription created successfully' };
    } else {
      console.error('❌ Failed to fix subscription:', result.error);
      return { success: false, error: result.error };
    }
  } catch (error) {
    console.error('❌ Error fixing subscription:', error);
    return { success: false, error: error.message };
  }
};


































